import React from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import Avatar from '@atlaskit/avatar';
import Badge from '@atlaskit/badge';
import TaskIcon from '@atlaskit/icon/core/task';
import TaskIconLegacy from '@atlaskit/icon/glyph/task';
import Lozenge from '@atlaskit/lozenge';
import { Text, Flex, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { blockedIssue_periscope_BlockedIssuesPanel$key } from '@atlassian/jira-relay/src/__generated__/blockedIssue_periscope_BlockedIssuesPanel.graphql';
import { messages } from './messages';

export const BlockedIssuesPanel = ({
	fragmentRef,
	blockedIssuesLength,
	index,
}: {
	fragmentRef: blockedIssue_periscope_BlockedIssuesPanel$key;
	blockedIssuesLength: number;
	index: number;
}) => {
	const { formatMessage } = useIntl();
	const data = useFragment(
		graphql`
			fragment blockedIssue_periscope_BlockedIssuesPanel on WorkSuggestionsBlockedIssue {
				issueKey
				title
				storyPoints
				issueIconUrl
				assignee {
					name
					pictureUrl
				}
				priority {
					iconUrl
					name
				}
			}
		`,
		fragmentRef,
	);
	return (
		<Flex
			direction="row"
			key={data.issueKey}
			justifyContent="space-between"
			xcss={[
				blockedIssuesLength !== 1 && index !== blockedIssuesLength - 1
					? blockedIssuesStyle
					: lastBlockedIssuesStyle,
			]}
		>
			<Flex alignItems="center" gap="space.100">
				{data.issueIconUrl ? (
					<img src={data.issueIconUrl} alt={data.title} />
				) : (
					<TaskIcon
						LEGACY_fallbackIcon={TaskIconLegacy}
						label={data.title}
						LEGACY_size="small"
						color={token('color.icon')}
					/>
				)}
				<Box xcss={blockedIssuesKeyStyle}>
					<Text maxLines={1}>{data.issueKey}</Text>
				</Box>
				<Text maxLines={2}>{data.title}</Text>
			</Flex>
			<Flex alignItems="center" gap="space.100">
				{data.priority?.iconUrl && (
					<img
						src={data.priority?.iconUrl}
						alt={data.priority?.name || ''}
						title={data.priority?.name || ''}
						width="22px"
						height="22px"
					/>
				)}
				<Flex justifyContent="center" xcss={storyPointsStyles}>
					<Badge>{data.storyPoints || '-'}</Badge>
				</Flex>
				<Lozenge appearance="removed">{formatMessage(messages.blockedLozengeNonFinal)}</Lozenge>

				{data.assignee?.pictureUrl ? (
					<Flex alignItems="center" xcss={assigneeStyles}>
						<Tooltip content={data.assignee.name} position="top">
							<Avatar
								appearance="circle"
								src={data.assignee.pictureUrl}
								label={data.assignee.name || ''}
								name={data.assignee.name || ''}
								size="small"
							/>
						</Tooltip>
					</Flex>
				) : (
					<Avatar label="" size="small" />
				)}
			</Flex>
		</Flex>
	);
};

const blockedIssuesKeyStyle = xcss({
	flexGrow: 1,
	flexShrink: 0,
});

const blockedIssuesStyle = xcss({
	width: '100%',
	padding: 'space.100',
	borderBottom: `1px solid ${token('color.border')}`,
});

const lastBlockedIssuesStyle = xcss({
	padding: 'space.100',
});

const storyPointsStyles = xcss({
	width: 'size.200',
});

const assigneeStyles = xcss({
	padding: 'space.025',
});
